import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Collapse,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Upload as UploadIcon,
  ContentPaste as PasteIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';

interface Meter {
  id: number;
  meter_name: string;
  meter_code: string;
  meter_type: string;
  unit: string;
}

interface Reading {
  id: number;
  meter_id: number;
  meter_name: string;
  meter_code: string;
  meter_type: string;
  unit: string;
  reading_date: string;
  reading_time: string;
  reading_value: number;
  previous_value: number | null;
  consumption: number | null;
  notes: string;
  entry_method: string;
  created_by_name: string;
  created_at: string;
}

const DataEntry: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [meters, setMeters] = useState<Meter[]>([]);
  const [readings, setReadings] = useState<Reading[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Manual entry form
  const [manualForm, setManualForm] = useState({
    meter_id: '',
    reading_date: format(new Date(), 'yyyy-MM-dd'),
    reading_time: '00:00',
    reading_value: '',
    notes: '',
  });

  // Bulk entry
  const [bulkData, setBulkData] = useState('');
  const [bulkPreview, setBulkPreview] = useState<any[]>([]);
  const [bulkDateFrom, setBulkDateFrom] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [bulkDateTo, setBulkDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));

  // CSV upload
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<any>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState<any>(null);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [showCsvErrors, setShowCsvErrors] = useState(false);
  const [csvImportYear, setCsvImportYear] = useState<number>(new Date().getFullYear());

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    fetchMeters();
    fetchReadings();
  }, []);

  const fetchMeters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/energy-management/meters', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMeters(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load meters');
    }
  };

  const fetchReadings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/energy-management/readings?limit=100', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReadings(response.data);
      setPage(0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load readings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(readings.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [readings.length, rowsPerPage, page]);

  const selectedManualMeter =
    meters.find((meter) => String(meter.id) === String(manualForm.meter_id)) || null;

  const handleManualSubmit = async () => {
    try {
      if (!manualForm.meter_id || !manualForm.reading_value) {
        setError('Please fill all required fields');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.post('/api/energy-management/readings', manualForm, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess('Reading added successfully');
      setManualForm({
        meter_id: '',
        reading_date: format(new Date(), 'yyyy-MM-dd'),
        reading_time: '00:00',
        reading_value: '',
        notes: '',
      });
      fetchReadings();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add reading');
    }
  };

  const handleBulkPaste = () => {
    try {
      const lines = bulkData.trim().split('\n');
      
      if (lines.length === 0) {
        throw new Error('No data to parse');
      }

      // Detect format type
      // Consumption records format starts with machine names (R1, R5, etc.) in first row
      // Simple format is tab-separated: meter_code<tab>date<tab>time<tab>value
      const firstLine = lines[0];
      const isConsumptionFormat = firstLine.includes(',R1,') || firstLine.includes(',R5,') || 
                                  firstLine.includes('STEAMER') || firstLine.includes('DATE,Prod.');

      if (isConsumptionFormat) {
        // Parse consumption records format
        parseConsumptionRecordsFormat(lines);
      } else {
        // Parse simple tab-separated format
        parseSimpleFormat(lines);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to parse bulk data');
      setBulkPreview([]);
    }
  };

  const parseSimpleFormat = (lines: string[]) => {
    // Expected format: meter_code<tab>date<tab>time<tab>value<tab>notes
    // Example: PWR-001	2026-05-28	12:00	12345.67	Daily reading
    const parsed: any[] = [];

    lines.forEach((line, index) => {
      if (line.trim() === '') return;

      const parts = line.split('\t');
      if (parts.length < 4) {
        throw new Error(`Line ${index + 1}: Invalid format (need at least 4 columns)`);
      }

      const [meter_code, date, time, value, notes = ''] = parts;
      const meter = meters.find((m) => m.meter_code === meter_code.trim());

      if (!meter) {
        throw new Error(`Line ${index + 1}: Meter code "${meter_code}" not found`);
      }

      parsed.push({
        meter_id: meter.id,
        meter_code: meter.meter_code,
        meter_name: meter.meter_name,
        reading_date: date.trim(),
        reading_time: time.trim() + ':00',
        reading_value: parseFloat(value.trim()),
        notes: notes.trim(),
      });
    });

    setBulkPreview(parsed);
    setSuccess(`Parsed ${parsed.length} readings successfully. Review and confirm to save.`);
  };

  const parseConsumptionRecordsFormat = (lines: string[]) => {
    // Column index mapping for consumption records format
    const COLUMN_MAPPINGS = [
      { index: 0, field: 'DATE' },
      // R1 Machine
      { index: 1, field: 'production', meterCode: 'R1-STEAM', productionType: 'Production', productionUnit: 'Mts' },
      { index: 2, field: 'reading', meterCode: 'R1-STEAM' },
      { index: 3, field: 'reading', meterCode: 'R1-POWER' },
      { index: 5, field: 'reading', meterCode: 'R1-WATER' },
      // R5 Machine
      { index: 6, field: 'production', meterCode: 'R5-STEAM', productionType: 'Production', productionUnit: 'Mts' },
      { index: 7, field: 'reading', meterCode: 'R5-STEAM' },
      { index: 8, field: 'reading', meterCode: 'R5-POWER' },
      { index: 9, field: 'reading', meterCode: 'R5-WATER' },
      // R3 Machine
      { index: 10, field: 'production', meterCode: 'R3-STEAM', productionType: 'Production', productionUnit: 'Mts' },
      { index: 11, field: 'reading', meterCode: 'R3-STEAM' },
      { index: 12, field: 'reading', meterCode: 'R3-POWER' },
      { index: 13, field: 'reading', meterCode: 'R3-WATER' },
      // R4 Machine
      { index: 14, field: 'production', meterCode: 'R4-STEAM', productionType: 'Production', productionUnit: 'Mts' },
      { index: 15, field: 'reading', meterCode: 'R4-STEAM' },
      { index: 16, field: 'reading', meterCode: 'R4-POWER' },
      { index: 17, field: 'reading', meterCode: 'R4-WATER' },
      // STEAMER 1
      { index: 18, field: 'production', meterCode: 'ST1-STEAM', productionType: 'After Printing', productionUnit: 'Mts' },
      { index: 19, field: 'production_lbs', meterCode: 'ST1-STEAM', productionType: 'Production', productionUnit: 'LBS' },
      { index: 20, field: 'reading', meterCode: 'ST1-STEAM' },
      { index: 21, field: 'reading', meterCode: 'ST1-POWER' },
      { index: 22, field: 'reading', meterCode: 'ST1-WATER' },
      // STEAMER 2
      { index: 23, field: 'production', meterCode: 'ST2-STEAM', productionType: 'After Printing', productionUnit: 'Mts' },
      { index: 24, field: 'production_lbs', meterCode: 'ST2-STEAM', productionType: 'Production', productionUnit: 'LBS' },
      { index: 25, field: 'reading', meterCode: 'ST2-STEAM' },
      { index: 26, field: 'reading', meterCode: 'ST2-POWER' },
      // NEW WASHER
      { index: 28, field: 'production', meterCode: 'WASH-STEAM', productionType: 'Production', productionUnit: 'Mts' },
      { index: 29, field: 'production_lbs', meterCode: 'WASH-STEAM', productionType: 'Production', productionUnit: 'LBS' },
      { index: 30, field: 'reading', meterCode: 'WASH-STEAM' },
      { index: 31, field: 'reading', meterCode: 'WASH-POWER' },
      { index: 32, field: 'reading', meterCode: 'WASH-WATER' },
      // Other meters
      { index: 33, field: 'reading', meterCode: 'CLR-POWER' },
      { index: 34, field: 'reading', meterCode: 'CLR-WATER' },
      { index: 35, field: 'reading', meterCode: 'SCR-HAND-WATER' },
      { index: 36, field: 'reading', meterCode: 'SCR-PRINT-POWER' },
      { index: 37, field: 'reading', meterCode: 'OFF-POWER' },
      { index: 38, field: 'reading', meterCode: 'DIG-AC-POWER' },
      { index: 39, field: 'reading', meterCode: 'SCR-STRIP-POWER' },
      { index: 40, field: 'reading', meterCode: 'SUBL-POWER' },
      { index: 41, field: 'reading', meterCode: 'DRYER-POWER' },
      { index: 42, field: 'reading', meterCode: 'DIG-PRINT-POWER' },
    ];

    const parsed: any[] = [];
    const meterLookup = new Map();
    meters.forEach(m => meterLookup.set(m.meter_code, m));

    // Get base date from date range selector
    const baseDate = new Date(bulkDateFrom);
    let currentDateOffset = 0;

    // Skip first 2 rows (headers), start from row 2
    for (let i = 2; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line || line.toLowerCase().includes('total')) continue;

      const columns = line.split(',');
      const dateStr = columns[0]?.trim();

      if (!dateStr) continue;

      // Calculate actual date using offset from base date
      const actualDate = new Date(baseDate);
      actualDate.setDate(actualDate.getDate() + currentDateOffset);
      currentDateOffset++;

      const readingDate = format(actualDate, 'yyyy-MM-dd');

      const productionDataMap: Map<string, { value: number; type: string; unit: string }> = new Map();

      // Process each column mapping
      for (const mapping of COLUMN_MAPPINGS) {
        if (mapping.field === 'DATE' || !mapping.meterCode) continue;

        const rawValue = columns[mapping.index]?.trim();
        if (!rawValue || rawValue === '' || rawValue === '0' || rawValue === '-') continue;

        const value = parseFloat(rawValue.replace(/,/g, '').replace(/\s+/g, ''));
        if (isNaN(value)) continue;

        // Store production data for later use
        if (mapping.field === 'production' || mapping.field === 'production_lbs') {
          const prodKey = `${mapping.meterCode}_${mapping.productionType}_${mapping.productionUnit}`;
          productionDataMap.set(prodKey, {
            value,
            type: mapping.productionType || 'Production',
            unit: mapping.productionUnit || 'Mts'
          });
          continue;
        }

        // Handle meter readings
        if (mapping.field === 'reading') {
          const meter = meterLookup.get(mapping.meterCode);
          if (!meter) continue;

          // Find associated production value (prioritize Mts over LBS)
          let productionValue = null;
          let productionUnit = null;
          let productionType = null;

          const prodKeyMts = `${mapping.meterCode}_Production_Mts`;
          const prodKeyAfterPrint = `${mapping.meterCode}_After Printing_Mts`;
          const prodKeyLBS = `${mapping.meterCode}_Production_LBS`;

          if (productionDataMap.has(prodKeyMts)) {
            const prod = productionDataMap.get(prodKeyMts)!;
            productionValue = prod.value;
            productionUnit = prod.unit;
            productionType = prod.type;
          } else if (productionDataMap.has(prodKeyAfterPrint)) {
            const prod = productionDataMap.get(prodKeyAfterPrint)!;
            productionValue = prod.value;
            productionUnit = prod.unit;
            productionType = prod.type;
          } else if (productionDataMap.has(prodKeyLBS)) {
            const prod = productionDataMap.get(prodKeyLBS)!;
            productionValue = prod.value;
            productionUnit = prod.unit;
            productionType = prod.type;
          }

          parsed.push({
            meter_id: meter.id,
            meter_code: meter.meter_code,
            meter_name: meter.meter_name,
            reading_date: readingDate,
            reading_time: '00:00:00',
            reading_value: value,
            production_value: productionValue,
            production_unit: productionUnit,
            production_type: productionType,
            notes: 'Bulk paste import',
          });
        }
      }
    }

    if (parsed.length === 0) {
      throw new Error('No valid data found in consumption records format');
    }

    setBulkPreview(parsed);
    setSuccess(`Parsed ${parsed.length} readings from consumption records format (${currentDateOffset} days). Review and confirm to save.`);
  };

  const handleBulkSubmit = async () => {
    try {
      if (bulkPreview.length === 0) {
        setError('No data to submit');
        return;
      }

      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/energy-management/readings/bulk',
        { readings: bulkPreview },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(
        `Bulk import completed: ${response.data.inserted} inserted, ${response.data.skipped} skipped`
      );
      setBulkData('');
      setBulkPreview([]);
      fetchReadings();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to import bulk data');
    }
  };

  const handleCSVUpload = async () => {
    if (!csvFile) {
      setCsvError('Please select a file');
      return;
    }

    setCsvUploading(true);
    setCsvError(null);
    setCsvResult(null);

    const formData = new FormData();
    formData.append('file', csvFile);

    try {
      const token = localStorage.getItem('token');
      
      // Determine endpoint based on file extension
      const isExcel = csvFile.name.endsWith('.xlsx') || csvFile.name.endsWith('.xls');
      const endpoint = isExcel 
        ? '/api/energy-management/readings/upload-excel'
        : '/api/energy-management/readings/upload-csv';

      if (!isExcel) {
        formData.append('import_year', String(csvImportYear));
      }
      
      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setCsvResult(response.data);
      setUploadProgress(response.data);
      
      if (response.data.imported > 0) {
        const fileType = isExcel ? 'Excel' : 'CSV';
        const sheetsInfo = response.data.sheets 
          ? ` from ${response.data.sheets.length} sheet(s)` 
          : '';
        setSuccess(
          `${fileType} import completed: ${response.data.imported} readings imported${sheetsInfo}, ${response.data.skipped} rows skipped`
        );
        setTimeout(() => {
          fetchReadings();
        }, 1500);
      }
    } catch (err: any) {
      setCsvError(err.response?.data?.error || 'Failed to upload file');
      setError(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setCsvUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/energy-management/csv-template', {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'energy_readings_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to download template');
    }
  };

  return (
    <Box>
      <Collapse in={!!success}>
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Collapse>
      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      </Collapse>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { fontWeight: 600 },
          }}
        >
          <Tab icon={<AddIcon />} iconPosition="start" label="Manual Entry" />
          <Tab icon={<PasteIcon />} iconPosition="start" label="Bulk Paste" />
          <Tab icon={<CloudUploadIcon />} iconPosition="start" label="CSV Upload" />
        </Tabs>

        {/* Manual Entry Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Add Single Reading
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={meters}
                  value={selectedManualMeter}
                  onChange={(_, newValue) => {
                    setManualForm({
                      ...manualForm,
                      meter_id: newValue ? String(newValue.id) : '',
                    });
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) =>
                    `${option.meter_name} (${option.meter_code}) - ${option.meter_type}`
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Search Meter" required />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Reading Date"
                  value={manualForm.reading_date}
                  onChange={(e) => setManualForm({ ...manualForm, reading_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="time"
                  label="Time"
                  value={manualForm.reading_time}
                  onChange={(e) => setManualForm({ ...manualForm, reading_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Reading Value"
                  value={manualForm.reading_value}
                  onChange={(e) => setManualForm({ ...manualForm, reading_value: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes (Optional)"
                  value={manualForm.notes}
                  onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleManualSubmit}
                  sx={{ backgroundColor: '#00695C' }}
                >
                  Add Reading
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Bulk Paste Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Paste Multiple Readings
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                Supported Formats:
              </Typography>
              
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                1. Simple Tab-Separated Format:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                meter_code &lt;TAB&gt; date &lt;TAB&gt; time &lt;TAB&gt; value &lt;TAB&gt; notes (optional)
                <br />
                Example: PWR-001 &nbsp;&nbsp; 2026-05-28 &nbsp;&nbsp; 12:00 &nbsp;&nbsp; 12345.67 &nbsp;&nbsp; Daily reading
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                2. Consumption Records Format (CSV):
              </Typography>
              <Typography variant="body2">
                Copy and paste entire CSV content including headers (two-row header with machine names and column descriptions).
                <br />
                System automatically maps columns to meters and extracts production data.
                <br />
                Use the date range below to specify the actual dates for the data.
              </Typography>
            </Alert>

            {/* Date Range Selector for Consumption Records */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Date Range for Consumption Records Format:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="From Date"
                    value={bulkDateFrom}
                    onChange={(e) => setBulkDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="First date in the pasted data"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="To Date"
                    value={bulkDateTo}
                    onChange={(e) => setBulkDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Last date in the pasted data (informational)"
                  />
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                <strong>Note:</strong> When you paste consumption records, the system will map each row sequentially starting from the "From Date". 
                For example, if you paste 28 rows starting from Feb 26, 2026, the dates will be Feb 26 through Mar 25, 2026.
              </Typography>
            </Paper>

            <TextField
              fullWidth
              multiline
              rows={10}
              label="Paste Data Here"
              value={bulkData}
              onChange={(e) => setBulkData(e.target.value)}
              placeholder="Paste consumption records CSV or tab-separated data..."
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button variant="outlined" startIcon={<PasteIcon />} onClick={handleBulkPaste}>
                Parse Data
              </Button>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={handleBulkSubmit}
                disabled={bulkPreview.length === 0}
                sx={{ backgroundColor: '#00695C' }}
              >
                Submit {bulkPreview.length} Readings
              </Button>
            </Box>

            {bulkPreview.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell>Meter</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell align="right">Value</TableCell>
                      <TableCell align="right">Production</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkPreview.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {row.meter_name} ({row.meter_code})
                        </TableCell>
                        <TableCell>{row.reading_date}</TableCell>
                        <TableCell>{row.reading_time}</TableCell>
                        <TableCell align="right">{row.reading_value.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          {row.production_value ? `${row.production_value.toLocaleString()} ${row.production_unit || ''}` : '-'}
                        </TableCell>
                        <TableCell>{row.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* CSV/Excel Upload Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Import Energy Data from CSV / Excel
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload your consumption records file (CSV or Excel). The system will automatically map columns to meters and import both meter readings and production data.
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Excel files:</strong> Each sheet can have a different month/year (e.g., "March 2026", "Apr 2026"). The system automatically detects the year and month from sheet names.
              </Alert>
            </Box>

            {/* File Upload */}
            <Box sx={{ mb: 3 }}>
              <input
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                id="csv-file-upload-input"
                type="file"
                onChange={(e) => {
                  const selectedFile = e.target.files?.[0];
                  if (selectedFile) {
                    const validExtensions = ['.csv', '.xlsx', '.xls'];
                    const isValid = validExtensions.some(ext => selectedFile.name.toLowerCase().endsWith(ext));
                    
                    if (isValid) {
                      setCsvFile(selectedFile);
                      setCsvError(null);
                      setCsvResult(null);
                    } else {
                      setCsvError('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
                      setCsvFile(null);
                    }
                  }
                }}
              />
              <label htmlFor="csv-file-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ py: 2, borderStyle: 'dashed', borderWidth: 2 }}
                >
                  {csvFile ? csvFile.name : 'Choose CSV or Excel File'}
                </Button>
              </label>
            </Box>

            {/* Upload Button */}
            {csvFile && csvFile.name.toLowerCase().endsWith('.csv') && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="CSV Import Year"
                  value={csvImportYear}
                  onChange={(e) => setCsvImportYear(parseInt(e.target.value, 10))}
                  helperText="CSV dates are in DD-MMM format. Select the correct year to avoid mixing data across years."
                >
                  {[csvImportYear - 2, csvImportYear - 1, csvImportYear, csvImportYear + 1, csvImportYear + 2].map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            <Box sx={{ mb: 3 }}>
              <Button
                variant="contained"
                onClick={handleCSVUpload}
                disabled={!csvFile || csvUploading}
                startIcon={csvUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                sx={{ backgroundColor: '#00695C' }}
                fullWidth
              >
                {csvUploading ? 'Importing...' : 'Import Data'}
              </Button>
            </Box>

            {/* Error Message */}
            {csvError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCsvError(null)}>
                {csvError}
              </Alert>
            )}

            {/* Success Result */}
            {csvResult && (
              <Box sx={{ mb: 2 }}>
                <Alert 
                  severity={csvResult.imported > 0 ? "success" : "warning"} 
                  icon={csvResult.imported > 0 ? <CheckIcon /> : <ErrorIcon />}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {csvResult.message}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={`Imported: ${csvResult.imported}`}
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Skipped: ${csvResult.skipped}`}
                      color="default"
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Total Rows: ${csvResult.totalRows}`}
                      color="info"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Alert>

                {/* Excel Sheet Results */}
                {csvResult.sheets && csvResult.sheets.length > 0 && (
                  <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Sheet Import Details:
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Sheet Name</TableCell>
                          <TableCell>Year</TableCell>
                          <TableCell>Month</TableCell>
                          <TableCell align="right">Imported</TableCell>
                          <TableCell align="right">Skipped</TableCell>
                          <TableCell align="right">Errors</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {csvResult.sheets.map((sheet: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell>{sheet.sheetName}</TableCell>
                            <TableCell>{sheet.year}</TableCell>
                            <TableCell>{sheet.month}</TableCell>
                            <TableCell align="right">{sheet.imported}</TableCell>
                            <TableCell align="right">{sheet.skipped}</TableCell>
                            <TableCell align="right">{sheet.errors}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                )}

                {/* Show Errors Toggle */}
                {csvResult.errors && csvResult.errors.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      onClick={() => setShowCsvErrors(!showCsvErrors)}
                      endIcon={showCsvErrors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      color="warning"
                    >
                      {showCsvErrors ? 'Hide' : 'Show'} Errors ({csvResult.errors.length})
                    </Button>

                    <Collapse in={showCsvErrors}>
                      <Paper variant="outlined" sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>#</TableCell>
                              <TableCell>Error Message</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {csvResult.errors.map((err: string, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>
                                  <Typography variant="caption" color="error">
                                    {err}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Collapse>
                  </Box>
                )}
              </Box>
            )}

            {/* File Format Guide */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                Expected File Format:
              </Typography>
              <Typography variant="caption" component="div" color="text.secondary" sx={{ mb: 2 }}>
                <strong>CSV Files:</strong>
                <br />
                • Two-row header: Machine names (row 1) and column descriptions (row 2)
                <br />
                • DATE column: DD-MMM format (e.g., 26-Feb, 27-Feb)
                <br />
                • Year selection: Use the CSV Import Year selector before uploading
                <br />
                • Year boundary detection: Auto-detects Dec → Jan transitions within the selected import year
                <br />
                • Meter columns: STEAM (kg), POWER (kwh), WATER, etc.
                <br />
                • Production columns: Prod.(Mts), A/Printing (Mts), prod (LBS), etc.
              </Typography>
              <Typography variant="caption" component="div" color="text.secondary">
                <strong>Excel Files (.xlsx, .xls):</strong>
                <br />
                • Same format as CSV, but supports multiple sheets
                <br />
                • Each sheet can represent a different month/year
                <br />
                • Sheet names should include year and month (e.g., "March 2026", "Apr 2026", "2026-03")
                <br />
                • System automatically extracts year/month from sheet names
                <br />
                • All sheets are processed in a single upload
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Recent Readings Table */}
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Recent Readings
          </Typography>
          <Button startIcon={<RefreshIcon />} onClick={fetchReadings} size="small">
            Refresh
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Meter</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell align="right">Previous</TableCell>
                <TableCell align="right">Consumption</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Created By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {readings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((reading) => (
                <TableRow key={reading.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {reading.meter_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {reading.meter_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={reading.meter_type} size="small" />
                  </TableCell>
                  <TableCell>{reading.reading_date}</TableCell>
                  <TableCell>{reading.reading_time}</TableCell>
                  <TableCell align="right">
                    <strong>{reading.reading_value}</strong> {reading.unit}
                  </TableCell>
                  <TableCell align="right">
                    {reading.previous_value ? `${reading.previous_value} ${reading.unit}` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {reading.consumption !== null ? (
                      <Chip
                        label={`${reading.consumption} ${reading.unit}`}
                        size="small"
                        color={reading.consumption > 0 ? 'success' : 'default'}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={reading.entry_method} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {reading.notes || '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{reading.created_by_name}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={readings.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default DataEntry;
